import { OwnerType, Recipe, RecipeType } from '@/types';
import { CHARACTERISTICS_MAP } from '../constants/characteristics';
import { formatDate } from './mask';
import { toast } from 'react-toastify';
import { clientLogger } from '@/lib/client-logger';

export const isAntibiotic = (product) => product?.antibiotico === true;
export const isPsychotropic = (product) => product?.psico_identifier === true;

export const needRecipeDelivery = (product) =>
  product?.psico_identifier === true;
export const needRecipe = (product) =>
  product?.antibiotico === true || product?.psico_identifier === true;

export const getRelatives = (profile) => {
  const { dynamicProperties } = profile;
  const profileRelatives = dynamicProperties?.filter(
    (e) => e.id === 'x_profile_relatives',
  )[0]?.value;
  const profileRelativesConverted =
    profileRelatives && JSON.parse(profileRelatives);

  return profileRelativesConverted || [];
};

export const objectHasValidValues = (obj) => {
  return Object.values(obj).some((value) => !!value);
};

export const convertTime = (minutes) => {
  const days = Math.floor(minutes / 1440);
  const remainingMinutes = minutes % 1440;
  const hours = Math.floor(remainingMinutes / 60);
  const finalMinutes = remainingMinutes % 60;

  const timeComponents = [];
  if (days > 0) {
    timeComponents.push(`${days} dia${days > 1 ? 's' : ''}`);
  }
  if (hours > 0) {
    timeComponents.push(`${hours} hora${hours > 1 ? 's' : ''}`);
  }
  if (finalMinutes > 0) {
    timeComponents.push(`${finalMinutes} minuto${finalMinutes > 1 ? 's' : ''}`);
  }

  if (timeComponents.length == 3) {
    let minutes = timeComponents.pop(1);

    return `${timeComponents.join(', ')} e ${minutes}`;
  }

  return timeComponents.join(' e ');
};

// TODO: increase function logic
export const validatePrescriptionDate = (
  value: string,
  hasPsicotropic: boolean,
  maxDays?: number,
) => {
  const userDate = value;
  const userDate2 = userDate.split('/');
  const finalDateStr = `${userDate2[1]}/${userDate2[0]}/${userDate2[2]}`;
  const finalDate = new Date(finalDateStr).getTime();
  const date = new Date();
  const maxDate = date.setDate(date.getDate() + 0);
  let minDate;

  // If maxDays is provided (from characteristic 33), use it
  // Otherwise, fall back to the existing logic (30 days for psicotropic, 10 days otherwise)
  if (maxDays !== undefined && maxDays > 0) {
    minDate = date.setDate(date.getDate() - maxDays);
  } else if (hasPsicotropic) {
    minDate = date.setDate(date.getDate() - 30);
  } else {
    minDate = date.setDate(date.getDate() - 10);
  }

  if (finalDate > maxDate || finalDate <= minDate) {
    return false;
  }

  return true;
};

export const transformRecipes = (recipes: any[]) => {
  if (!recipes || !Array.isArray(recipes)) {
    return [];
  }

  return recipes
    .map((recipe) => {
      if (!recipe) {
        return null;
      }

      // Safely handle type conversion
      const recipeType = recipe.type
        ? (typeof recipe.type === 'string'
          ? recipe.type.toLowerCase()
          : String(recipe.type).toLowerCase())
        : 'physical';

      const file = recipe?.file;
      const isPhysical = recipeType !== RecipeType.DIGITAL;
      const attachmentId = file?.id != null ? file.id : null;
      // Exclude physical recipes without attachment (e.g. after user removed file or stale persisted state)
      if (isPhysical && attachmentId == null) {
        return null;
      }
      return {
        name: recipe.name || '',
        document: recipe.rg || '',
        type: recipeType === RecipeType.DIGITAL ? 'digital' : 'physical',
        date: recipe.date ? formatDate(recipe.date) : '',
        token: recipe?.token || null,
        attachment_id: attachmentId,
        user_relationship_id: recipe?.relative_id || null,
      };
    })
    .filter((recipe): recipe is NonNullable<typeof recipe> => recipe !== null);
};

export function getRecipeRequirement(
  characteristics: { id: number | string; value: string | boolean }[],
) {
  const characteristicsMap = characteristics?.reduce<Record<string | number, boolean>>(
    (acc, char) => {
      const value =
        typeof char.value === 'string'
          ? char.value.toLowerCase() === 'true'
          : char.value;
      acc[char.id] = value;
      return acc;
    },
    {},
  );

  return {
    antibiotic:
      characteristicsMap?.[CHARACTERISTICS_MAP.ANTIBIOTICO.id] || false,
    psychotropic:
      characteristicsMap?.[CHARACTERISTICS_MAP.PSICOTROPICO.id] || false,
  };
}


interface SaveRecipeParams {
  formValues: any;
  savedRecipes: Recipe[];
  setSavedRecipes: (recipes: Recipe[]) => void;
  recipeMetadata: object;
  selectedOwnerType: string;
  selectedRecipeType: string | null;
  onReset: () => void;
  me?: { first_name: string };
  createUserRelationship: (data: any) => Promise<any>;
  updateUserRelationship: (data: any) => Promise<any>;
}

export const saveRecipe = async ({
  formValues,
  savedRecipes,
  setSavedRecipes,
  recipeMetadata,
  selectedOwnerType,
  selectedRecipeType,
  onReset,
  me,
  createUserRelationship,
  updateUserRelationship,
}: SaveRecipeParams) => {
  const recipeId = formValues.id || Date.now().toString();

  const baseRecipe = {
    id: recipeId,
    date: formValues.date || '',
    ownerType: selectedOwnerType as OwnerType,
    type: selectedRecipeType as RecipeType,
  };

  let ownerData = {};

  if (selectedOwnerType === OwnerType.USER) {
    ownerData = { rg: formValues.rg, name: me?.first_name };
  } else {
    try {
      let relativeId = formValues.relative_id;

      if (relativeId) {
        await updateUserRelationship({
          relative_id: relativeId,
          name: formValues.relative_name,
          document: formValues.relative_rg,
          document_type: 'rg',
          birth_date: formatDate(formValues.relative_birthdate),
          relationship: formValues.relative_parentesco,
          phone: formValues.relative_phone,
        });
      } else {
        const newRelation = await createUserRelationship({
          name: formValues.relative_name,
          document: formValues.relative_rg,
          document_type: 'rg',
          birth_date: formatDate(formValues.relative_birthdate),
          relationship: formValues.relative_parentesco,
          phone: formValues.relative_phone,
        });

        relativeId = newRelation?.id;
      }

      ownerData = {
        relative_id: relativeId,
        relative_name: formValues.relative_name,
        name: formValues.relative_name,
        relative_rg: formValues.relative_rg,
        rg: formValues.relative_rg,
        relative_birthdate: formValues.relative_birthdate,
        relative_parentesco: formValues.relative_parentesco,
        relative_phone: formValues.relative_phone,
      };
    } catch (error: any) {
      clientLogger.error('Checkout recipe: save recipe failed (user relationship)', {
        email: (me as any)?.email,
        responseData: error?.response?.data,
        status: error?.response?.status,
      });
      toast.error(
        'Erro ao criar relacionamento do usuário, prossiga com o cadastro da receita!',
      );
      return;
    }
  }

  const newRecipe: Recipe = {
    ...baseRecipe,
    ...ownerData,
    ...recipeMetadata,
  };

  const recipeExists = savedRecipes.some((recipe) => recipe.id === recipeId);

  if (recipeExists) {
    setSavedRecipes(
      savedRecipes.map((recipe) =>
        recipe.id === recipeId ? newRecipe : recipe,
      ),
    );
  } else {
    setSavedRecipes([...savedRecipes, newRecipe]);
  }

  onReset();
};
